import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import '@ag-grid-community/styles/ag-grid.css';
import '@ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact, CustomCellRendererProps } from '@ag-grid-community/react';
import {
  CellKeyDownEvent,
  ColDef,
  GetRowIdParams,
  GridReadyEvent,
  GridSizeChangedEvent,
  KeyCreatorParams,
  ValueFormatterParams
} from '@ag-grid-community/core';
import {
  formatLineItemType,
  LineItemType,
  lineItemTypeOptions
} from 'shared/src/domain/line-item/line-item-types';
import {
  allTactics,
  allUnitPriceTypes,
  Channel
} from 'shared/src/domain/line-item/line-item-channels';
import { useChannelRatesContext } from '@/features/strategy/channel-rates-context';
import { formatDateString } from 'shared/src/utils/date/date-utils';
import { useUrlTableState } from '@/components/tables/table-state-persistence';
import { useProposalContext } from '@/features/proposals/proposal-context';
import { DBStrategy } from 'shared/src/types';
import {
  isAgencyMarkupStrategy,
  isReferralCommissionStrategy
} from '@/features/strategy/strategy-utils';
import {
  AgencyMarkupRenderer,
  AgencyMarkupTooltip,
  CellRendererProps,
  EffectivePriceRenderer,
  EffectivePriceTooltip,
  EffectiveUnitPriceRenderer,
  EffectiveUnitPriceTooltip,
  EstimatedUnitsRenderer,
  EstimatedUnitsTooltip,
  GrossPriceRenderer,
  GrossPriceTooltip,
  GrossUnitPriceRenderer,
  GrossUnitPriceTooltip,
  MediaBudgetRenderer,
  MediaBudgetTooltip,
  NullPacingDetails,
  PlannedMarginCellRenderer,
  PlannedMarginTooltip,
  PlannedUnitCostRenderer,
  PlannedUnitCostTooltip,
  PriceCellRenderer,
  PriceTooltip,
  ReferralFeeRenderer,
  ReferralFeeTooltip,
  TargetMarginCellRenderer,
  TargetMarginTooltip,
  TargetUnitCostCellRenderer,
  TargetUnitCostTooltip,
  TargetUnitPriceCellRenderer,
  TargetUnitPriceTooltip,
  UnitPriceCellRenderer,
  UnitPriceTooltip
} from '@/features/line-items/line-item-table-cell-components';
import { LineItemActionsV2 } from '@/features/line-items/line-item-actions';
import { generateGraph } from '@/features/proposals/line-item-table-utils';
import { LineItemTrpcResponse } from 'shared/src/trpc-types/line-item-trpc-types';

const defaultColDef: ColDef = {
  maxWidth: 250,
  suppressHeaderContextMenu: true,
  suppressHeaderMenuButton: true,
  editable: false
};

type Props = {
  lineItems: LineItemTrpcResponse[];
  setSelectedRows: (rows: LineItemTrpcResponse[]) => void;
  setIsEditing: (lineItemId: string) => void;
  handleKeyboardDuplicate: (currentRow: LineItemTrpcResponse) => void;
  hideRowActions: boolean;
};

export function ProposalLineItemsTable({
  lineItems,
  setSelectedRows,
  setIsEditing,
  handleKeyboardDuplicate,
  hideRowActions
}: Props) {
  const { proposal, diff } = useProposalContext();
  const { initialState, storeGridState } = useUrlTableState();
  const gridRef = useRef<AgGridReact<LineItemTrpcResponse>>(null);
  const { channels } = useChannelRatesContext();

  const columnDefs: ColDef<LineItemTrpcResponse>[] = useMemo(
    () => createColumnDefs(channels, setIsEditing, proposal),
    [channels, proposal, setIsEditing]
  );

  useEffect(() => {
    // ag-grid sometimes does not properly refresh cells after the data
    // has changed, resulting in styling not being applied correctly.
    // this effect forces a refresh when lineItems changes, resolving the issue
    gridRef.current?.api?.refreshCells({ force: true });
  }, [lineItems]);

  useEffect(() => {
    if (hideRowActions) gridRef.current?.api?.setColumnsVisible(['actions'], false);
    else gridRef.current?.api?.setColumnsVisible(['actions'], true);
  }, [hideRowActions]);

  const getRowId = useCallback(
    (params: GetRowIdParams<LineItemTrpcResponse>) => params.data.id,
    []
  );

  const onSelectionChanged = useCallback(() => {
    const updatedRows: LineItemTrpcResponse[] = gridRef.current!.api.getSelectedRows();
    setSelectedRows(updatedRows);
  }, [setSelectedRows]);

  const onCellKeyDown = useCallback(
    (e: CellKeyDownEvent<LineItemTrpcResponse>) => {
      if (!e.event) return;
      const keyboardEvent = e.event as unknown as KeyboardEvent;
      const { code, metaKey, ctrlKey } = keyboardEvent;
      const { data } = e.node;
      if (code === 'KeyD' && (metaKey || ctrlKey) && data) {
        handleKeyboardDuplicate(data);
      }
    },
    [handleKeyboardDuplicate]
  );

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      gridRef.current?.api?.autoSizeAllColumns();
    }, 250);
    return () => clearTimeout(timeoutId);
  }, [lineItems]);

  const onGridReady = useCallback((event: GridReadyEvent) => {
    // Expose the grid API to the window for Playwright testing
    (window as any).agGridApi = event.api;
  }, []);

  // AGGrid will unpin pinned columns when you resize the window but will not re-pin them
  const onGridSizeChanged = useCallback((e: GridSizeChangedEvent) => {
    e.api.setColumnsPinned(['name'], 'left');
    e.api.setColumnsPinned(['actions'], 'right');
  }, []);

  const getRowStyle = useCallback(
    (params: { data?: LineItemTrpcResponse }) => {
      if (!params.data) return undefined;

      const lineItemId = params.data.id;
      const isNew = diff.line_items.to_create.some(item => item.id === lineItemId);
      const isEdited = diff.line_items.to_update.some(item => item.id === lineItemId);

      if (isNew) {
        return { background: '#f0fdf4' }; // green tint for new items
      }
      if (isEdited) {
        return { background: '#fffbeb' }; // orange/amber tint for edited items
      }
      return undefined;
    },
    [diff]
  );

  return (
    <div className="flex h-full w-full flex-col">
      <div className="ag-theme-quartz" style={{ height: '100%', width: '100%' }}>
        <AgGridReact
          ref={gridRef}
          rowData={lineItems}
          columnDefs={columnDefs}
          gridOptions={{ headerHeight: 36, rowHeight: 42 }}
          suppressRowVirtualisation={true}
          suppressColumnVirtualisation={true}
          stopEditingWhenCellsLoseFocus={true}
          defaultColDef={defaultColDef}
          domLayout={'normal'}
          rowSelection={{ mode: 'multiRow', headerCheckbox: true, enableClickSelection: false }}
          selectionColumnDef={{ pinned: 'left' }}
          onSelectionChanged={onSelectionChanged}
          getRowId={getRowId}
          getRowStyle={getRowStyle}
          tooltipShowDelay={0}
          onCellKeyDown={onCellKeyDown}
          initialState={initialState}
          onStateUpdated={storeGridState}
          onGridReady={onGridReady}
          onGridSizeChanged={onGridSizeChanged}
        />
      </div>
    </div>
  );
}

function createColumnDefs(
  channels: Channel[],
  setIsEditing: (lineItemId: string) => void,
  proposal: DBStrategy
): ColDef<LineItemTrpcResponse>[] {
  const tactics = allTactics(channels);
  const unitPriceTypes = allUnitPriceTypes(channels);
  const hasReferralCommission = isReferralCommissionStrategy(proposal);
  const hasAgencyMarkup = isAgencyMarkupStrategy(proposal);

  return [
    {
      field: 'name',
      headerName: 'Line Item Name',
      pinned: 'left',
      maxWidth: 500
    },
    {
      field: 'line_item_type',
      headerName: 'Line Item Type',
      maxWidth: 150,
      valueFormatter: params => formatLineItemType(params.value),
      filter: 'agSetColumnFilter',
      filterParams: {
        values: lineItemTypeOptions,
        valueFormatter: (params: ValueFormatterParams<LineItemType>) =>
          formatLineItemType(params.value)
      }
    },
    {
      field: 'price',
      headerName: 'Price',
      cellDataType: 'number',
      cellRenderer: PriceCellRenderer,
      filter: 'agNumberColumnFilter',
      filterParams: {
        allowedCharPattern: '\\d\\-\\,'
      },
      tooltipComponent: PriceTooltip,
      tooltipField: 'price'
    },
    {
      field: 'effective_price',
      headerName: 'Effective Price',
      hide: !hasReferralCommission,
      cellDataType: 'number',
      cellRenderer: EffectivePriceRenderer,
      tooltipComponent: EffectivePriceTooltip,
      tooltipComponentParams: { referralCommissionRate: proposal.referral_commission_rate },
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'referral_commission_amount',
      headerName: 'Referral Fee',
      hide: !hasReferralCommission,
      cellDataType: 'number',
      cellRenderer: ReferralFeeRenderer,
      tooltipComponent: ReferralFeeTooltip,
      tooltipComponentParams: { referralCommissionRate: proposal.referral_commission_rate },
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      headerName: 'Agency Markup',
      hide: !hasAgencyMarkup,
      cellDataType: 'number',
      cellRenderer: AgencyMarkupRenderer,
      tooltipComponent: AgencyMarkupTooltip,
      tooltipComponentParams: { agencyMarkupRate: proposal.agency_markup_rate }
    },
    {
      field: 'gross_price',
      headerName: 'Gross Price',
      hide: !hasAgencyMarkup,
      cellDataType: 'number',
      cellRenderer: GrossPriceRenderer,
      tooltipComponent: GrossPriceTooltip,
      tooltipComponentParams: { agencyMarkupRate: proposal.agency_markup_rate },
      tooltipValueGetter: params => params.value || 'no value'
    },
    // TODO[mk] - channel, tactic, unit_price_id keyCreators and comparators need to be defined
    {
      field: 'channel_name',
      headerName: 'Channel',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: channels,
        valueFormatter: (params: ValueFormatterParams<Channel>) => params.value.name
      }
    },
    {
      field: 'tactic_name',
      headerName: 'Tactic',
      filter: 'agSetColumnFilter',
      filterParams: {
        values: tactics,
        keyCreator: (params: KeyCreatorParams) => params.value.id,
        valueFormatter: (params: ValueFormatterParams) => params.value.name
      }
    },
    {
      field: 'unit_price_type_name',
      headerName: 'Unit Price Type',
      minWidth: 200,
      filter: 'agSetColumnFilter',
      filterParams: {
        values: unitPriceTypes,
        keyCreator: (params: KeyCreatorParams) => params.value.id,
        valueFormatter: (params: ValueFormatterParams) => params.value.name
      }
    },
    {
      field: 'target_unit_price',
      headerName: 'Target Unit Price',
      cellDataType: 'number',
      tooltipComponent: TargetUnitPriceTooltip,
      tooltipComponentParams: { channels },
      tooltipValueGetter: params => params.value || 'no value',
      cellRenderer: TargetUnitPriceCellRenderer
    },
    {
      field: 'unit_price',
      headerName: 'Unit Price',
      cellDataType: 'number',
      tooltipComponent: UnitPriceTooltip,
      tooltipComponentParams: { channels },
      tooltipValueGetter: params => params.value || 'no value', // need to pass dummy value here so tooltip will still render
      cellRenderer: (params: CellRendererProps<number>) => (
        <UnitPriceCellRenderer channels={channels} {...params} />
      )
    },
    {
      field: 'gross_unit_price',
      headerName: 'Gross Unit Price',
      hide: !hasAgencyMarkup,
      cellDataType: 'number',
      cellRenderer: GrossUnitPriceRenderer,
      tooltipComponent: GrossUnitPriceTooltip,
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'effective_unit_price',
      headerName: 'Effective Unit Price',
      hide: !hasReferralCommission,
      cellDataType: 'number',
      cellRenderer: EffectiveUnitPriceRenderer,
      tooltipComponent: EffectiveUnitPriceTooltip,
      tooltipValueGetter: params => params.value || 'no value'
    },
    {
      field: 'estimated_units',
      headerName: 'Estimated Units',
      cellDataType: 'number',
      cellRenderer: EstimatedUnitsRenderer,
      tooltipComponent: EstimatedUnitsTooltip,
      tooltipField: 'estimated_units'
    },
    {
      field: 'media_budget',
      headerName: 'Media Budget',
      cellRenderer: MediaBudgetRenderer,
      tooltipComponent: MediaBudgetTooltip,
      tooltipComponentParams: { referralCommissionRate: proposal.referral_commission_rate },
      tooltipField: 'media_budget'
    },
    {
      field: 'target_unit_cost',
      headerName: 'Target Unit Cost',
      cellDataType: 'number',
      tooltipComponent: TargetUnitCostTooltip,
      tooltipComponentParams: { channels },
      tooltipValueGetter: params => params.value || 'no value',
      cellRenderer: TargetUnitCostCellRenderer
    },
    {
      field: 'planned_unit_cost',
      headerName: 'Planned Unit Cost',
      cellRenderer: (params: CellRendererProps<number>) => (
        <PlannedUnitCostRenderer channels={channels} {...params} />
      ),
      tooltipComponent: PlannedUnitCostTooltip,
      tooltipComponentParams: {
        referralCommissionRate: proposal.referral_commission_rate,
        channels
      },
      tooltipField: 'planned_unit_cost'
    },
    {
      field: 'target_margin',
      headerName: 'Target Margin',
      cellDataType: 'number',
      tooltipComponent: TargetMarginTooltip,
      tooltipValueGetter: params => params.value || 'no value',
      cellRenderer: TargetMarginCellRenderer
    },
    {
      field: 'planned_margin',
      headerName: 'Planned Margin',
      cellDataType: 'number',
      editable: false, // Now a calculated field, not user-editable
      tooltipComponent: PlannedMarginTooltip,
      tooltipValueGetter: params => params.value || 'no value', // need to pass dummy value here so tooltip will still render
      cellRenderer: (params: CellRendererProps<number>) => (
        <PlannedMarginCellRenderer channels={channels} {...params} />
      )
    },
    {
      cellDataType: 'date',
      field: 'dates.from',
      headerName: 'Start Date',
      valueFormatter: params => formatDateString(params.value)
    },
    {
      cellDataType: 'date',
      field: 'dates.to',
      headerName: 'End Date',
      valueFormatter: params => formatDateString(params.value)
    },
    {
      field: 'pacing_type',
      headerName: 'Pacing Type',
      filter: 'agSetColumnFilter',
      filterParams: { values: ['lifetime', 'custom'] }
    },
    {
      headerName: 'Pacing Details',
      minWidth: 200,
      valueGetter: ({ data }) => (data ? generateGraph(data) : undefined),
      cellRendererParams: { sparklineOptions: { type: 'area' } },
      cellRendererSelector: ({ data }) => {
        if (data?.pacing_type === 'custom') return { component: 'agSparklineCellRenderer' };
        else return { component: NullPacingDetails };
      }
    },
    {
      field: 'geo'
    },
    {
      field: 'targeting'
    },
    {
      field: 'audience'
    },
    {
      field: 'ad_formats',
      headerName: 'Ad Formats'
    },
    {
      field: 'purchase_order_number',
      headerName: 'PO Number (optional)'
    },
    {
      field: 'notes',
      headerName: 'Team Notes (optional)',
      maxWidth: 300
    },
    {
      field: 'media_platforms',
      headerName: 'Media Platforms',
      cellRenderer: ({ data }: CustomCellRendererProps<LineItemTrpcResponse>) =>
        data?.media_platforms.map(t => t.name).join(',')
    },
    {
      cellRenderer: ({ data }: CustomCellRendererProps<LineItemTrpcResponse>) =>
        data && <LineItemActionsV2 data={data} setIsEditing={setIsEditing} />,
      colId: 'actions',
      cellStyle: { padding: 0, border: 'none' },
      headerName: '',
      resizable: false,
      pinned: 'right',
      suppressHeaderFilterButton: true,
      suppressHeaderContextMenu: true,
      suppressMovable: true
    }
  ];
}
